/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Skip-to-Main-Content link (WCAG 2.1 AA, SC 2.4.1 Bypass Blocks)
 *
 * The first focusable element on every page. Hidden until it receives focus
 * (Tab from URL bar), then absolutely positioned over the top-left corner.
 * Activating it moves focus to the <main id={MAIN_CONTENT_ID}> landmark so
 * keyboard users can bypass repeated navigation.
 */
import React from "react";

export const MAIN_CONTENT_ID = "main-content";

const SkipToMainContent: React.FC = () => {
  // Use onClick to manage focus explicitly: anchor `href="#main-content"`
  // moves the visual scroll target but does NOT move keyboard focus reliably
  // across all browsers. We focus the landmark ourselves so SR users land
  // inside <main>.
  const handleActivate = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (main) {
      main.focus();
      // scrollIntoView is unavailable in JSDOM; guard so unit tests don't
      // crash, real browsers always have it.
      if (typeof main.scrollIntoView === "function") {
        main.scrollIntoView({ block: "start" });
      }
    }
  };

  return (
    <a
      href={`#${MAIN_CONTENT_ID}`}
      onClick={handleActivate}
      data-testid="skip-to-main-content"
      className={
        // Off-screen by default; visible + on-canvas when focused.
        "sr-only focus:not-sr-only " +
        "focus:fixed focus:top-2 focus:left-2 focus:z-[100] " +
        "focus:rounded-md focus:bg-background focus:px-4 focus:py-2 " +
        "focus:text-sm focus:font-medium focus:text-foreground " +
        "focus:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
      }
    >
      Skip to main content
    </a>
  );
};

export default SkipToMainContent;
